import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { CheckCircle, XCircle, Clock } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [orderInfo, setOrderInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<"success" | "pending" | "failed">("pending");

  useEffect(() => {
    const orderId = searchParams.get("orderId");
    const success = searchParams.get("success");
    const error = searchParams.get("error");

    if (error) {
      setPaymentStatus("failed");
      setLoading(false);
      return;
    }

    if (orderId) {
      fetchOrderStatus(orderId, success === "true" || success === "TRADE_SUCCESS");
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const fetchOrderStatus = async (orderId: string, isSuccess: boolean) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      const resp = await fetch(`/api/trade-orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();

      if (data.ok && data.data) {
        const order = data.data;
        setOrderInfo(order);

        // 判断支付状态
        if (order.payment_status === '已支付') {
          setPaymentStatus("success");
        } else if (order.payment_status === '支付失败') {
          setPaymentStatus("failed");
        } else if (isSuccess && !order.payment_status) {
          // 本地测试环境：如果同步回调显示成功但数据库还没更新，等待一下再查询
          setTimeout(() => {
            fetchOrderStatus(orderId, true);
          }, 2000);
          setPaymentStatus("pending");
        } else {
          setPaymentStatus("pending");
        }
      } else {
        setPaymentStatus("failed");
      }
    } catch (err) {
      console.error('获取订单状态失败:', err);
      setPaymentStatus("failed");
    } finally {
      setLoading(false);
    }
  };

  // 格式化金额
  const formatMoney = (amount: number | null) => {
    if (amount === null || amount === undefined) return "-";
    return new Intl.NumberFormat("zh-CN", {
      style: "currency",
      currency: "CNY",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // 下载投标文件
  const handleDownloadFile = async () => {
    if (!orderInfo || !orderInfo.order_id) {
      alert('订单编号不存在');
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("请先登录");
        navigate("/my-bids");
        return;
      }

      const resp = await fetch(`/api/trade-orders/${orderInfo.order_id}/download-file`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await resp.json();
      
      if (data.ok && data.data && data.data.downloadUrl) {
        // 触发下载
        const link = document.createElement('a');
        link.href = data.data.downloadUrl;
        link.download = data.data.downloadUrl.split('/').pop() || '投标文件.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert(data.error || '未找到投标文件');
      }
    } catch (err) {
      console.error('下载文件失败:', err);
      alert('下载失败，请稍后重试');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header simple />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#003c8c]"></div>
          <p className="mt-4 text-gray-500">正在查询订单状态...</p>
        </div>
      </div>
    );
  }

  // 支付成功
  if (paymentStatus === "success" && orderInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header simple />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-lg mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12" style={{ color: '#16a34a' }} />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#16a34a' }}>支付成功</h2>
            <p className="text-gray-600 mb-6">您已成功报名参与该项目</p>

            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <div className="flex justify-between mb-2">
                <span className="text-gray-500">订单编号</span>
                <span className="text-gray-800 font-mono">{orderInfo.order_id}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-500">支付金额</span>
                <span className="text-orange-500 font-bold">
                  {formatMoney(orderInfo.order_amount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">项目名称</span>
                <span className="text-gray-800 text-right max-w-[200px] truncate">
                  {orderInfo.project_name}
                </span>
              </div>
            </div>

            <div className="mb-6 flex justify-center">
              <button
                onClick={handleDownloadFile}
                style={{
                  width: '40vw',
                  height: '36px',
                  padding: '0 24px',
                  backgroundColor: '#16a34a',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 500,
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#15803d'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
              >
                下载招标文件
              </button>
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate("//my-bids")}
              >
                查看我的投标
              </Button>
              <Button
                className="flex-1 bg-[#003c8c] hover:bg-[#0050b8]"
                onClick={() => navigate("/trade-announcements")}
              >
                继续浏览公告
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 支付失败
  if (paymentStatus === "failed") {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header simple />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-lg mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-12 h-12" style={{ color: '#dc2626' }} />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#dc2626' }}>支付失败</h2>
            <p className="text-gray-600 mb-6">
              {orderInfo?.payment_status === '支付失败' 
                ? '订单已超时，请重新下单' 
                : '支付过程中出现问题，请稍后重试'}
            </p>

            {orderInfo && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-500">订单编号</span>
                  <span className="text-gray-800 font-mono">{orderInfo.order_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">项目名称</span>
                  <span className="text-gray-800 text-right max-w-[200px] truncate">
                    {orderInfo.project_name}
                  </span>
                </div>
              </div>
            )}

            <div className="flex gap-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate("/trade-announcements")}
              >
                返回公告列表
              </Button>
              {orderInfo && (
                <Button
                  className="flex-1 bg-[#003c8c] hover:bg-[#0050b8]"
                  onClick={() => navigate(`/payment?orderId=${orderInfo.order_id}`)}
                >
                  重新支付
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 支付中（等待回调）
  return (
    <div className="min-h-screen bg-gray-50">
      <Header simple />
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-lg mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-yellow-100 rounded-full flex items-center justify-center">
            <Clock className="w-12 h-12" style={{ color: '#f59e0b' }} />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#f59e0b' }}>支付处理中</h2>
          <p className="text-gray-600 mb-6">正在确认您的支付状态，请稍候...</p>

          {orderInfo && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <div className="flex justify-between mb-2">
                <span className="text-gray-500">订单编号</span>
                <span className="text-gray-800 font-mono">{orderInfo.order_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">项目名称</span>
                <span className="text-gray-800 text-right max-w-[200px] truncate">
                  {orderInfo.project_name}
                </span>
              </div>
            </div>
          )}

          <div className="mb-6">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#f59e0b]"></div>
          </div>

          <Button
            className="w-full bg-[#003c8c] hover:bg-[#0050b8]"
            onClick={() => {
              if (orderInfo) {
                fetchOrderStatus(orderInfo.order_id, true);
              }
            }}
          >
            刷新状态
          </Button>
        </div>
      </div>
    </div>
  );
}

